import Alpine from 'alpinejs'

import { getCalculatedDeliveryDates } from '@/clients/pops/client'
import {
  CART_INITIALISED,
  CART_UPDATED,
  NOTICE_ADD_TO_CART,
  SHOW_CART_MODAL,
} from '@/constants/cart-events'
import { MEAL_PROGRAM_SELLING_PLAN_ID } from '@/constants/commerce'
import {
  UPDATE_ACTIVE_ADDONS,
  UPDATE_ACTIVE_PROGRAM,
  UPDATE_ACTIVE_VARIANT,
  VARIANT_DOWNGRADED,
  UPDATE_PROGRAM_PRICE,
  UPDATE_ACTIVE_MEALS,
  UPDATE_DELIVERY_WINDOW,
  SHOW_PROGRAM_INFO_MODAL,
  ALIGN_ADDED_SKU_WITH_HOLIDAY_SCHEDULE,
  INIT_BUYING_OPTION,
  UPDATE_PROGRAM_COMPARE_AT_PRICE,
} from '@/constants/program-events'
import { DUAL_ZIP_CODE_MESSAGE } from '@/constants/programs'
import { addItems } from '@/scripts/cart'
import { sendInitialProductView } from '@/scripts/tracking'
import { chord } from '@/scripts/tracking/chord'
import {
  transformToChordItem,
  transformActiveVariantToGTMItem,
} from '@/scripts/tracking/helper'
import { $customer } from '@/stores/account/stores'
import type {
  CMSAddonsOptionGroup,
  CMSProgramAddonOption,
} from '@/types/sanity'
import { variantIsSubscription, getCartZipCode } from '@/utilities/commerce'
import { captureException } from '@/utilities/logger-fe'
import { formatPrice } from '@/utilities/number'
import { getDaysOfFoodFromSku, getWeekOfDate } from '@/utilities/sakara'
import { openDialog } from '@/stores/dialog/actions'

document.addEventListener('alpine:init', () => {
  Alpine.data('programPanel', () => ({
    cartZipCode: undefined,
    programType: null,
    optionGroupContentTypes: null,
    activeVariant: null,
    loading: true,
    groupedAddonsWithQuantity: {},
    addonGroups: [],
    isTracked: false,
    selectedOptionProperties: {},
    deliveryProperties: null,
    zipCode: null,
    activeBuyingOption: '',
    selectedDate: null,
    variantBeforeDowngrade: null,
    isFirstLoad: true,
    applyRedemption: false,
    customer: $customer.get(),
    addingToCart: false,

    async init() {
      this.programConfig =
        typeof this.$el.dataset.programConfig === 'string' &&
        JSON.parse(this.$el.dataset.programConfig)
      this.programType = this.programConfig.programType
      this.optionGroupContentTypes = this.programConfig.optionGroupContentTypes

      this.$watch('activeVariant', () => {
        sendInitialProductView({
          data: transformActiveVariantToGTMItem(this.activeVariant),
        })
      })
    },

    eventHandlers: {
      [`@${UPDATE_ACTIVE_ADDONS}.window`]() {
        const {
          group,
          addonsWithQuantity,
        }: {
          group: CMSAddonsOptionGroup
          addonsWithQuantity: {
            addon: CMSProgramAddonOption
            quantity: number
          }[]
        } = this.$event.detail

        if (!addonsWithQuantity.filter((item) => item).length) {
          delete this.groupedAddonsWithQuantity[group._id]
          this.$dispatch(UPDATE_PROGRAM_PRICE, this.programPrice)
          this.$dispatch(
            UPDATE_PROGRAM_COMPARE_AT_PRICE,
            this.programCompareAtPrice,
          )
          return
        }

        this.groupedAddonsWithQuantity[group._id] = {
          addons: addonsWithQuantity,
          group,
        }

        if (!this.isFirstLoad) {
          chord?.trackProductClicked({
            cart: {},
            product: transformToChordItem(
              this.activeVariant,
              null,
              this.groupedAddonsWithQuantity,
              this.selectedOptionProperties.meals,
            ),
          })
        } else {
          this.isFirstLoad = false
        }

        this.$dispatch(UPDATE_PROGRAM_PRICE, this.programPrice)
        this.$dispatch(
          UPDATE_PROGRAM_COMPARE_AT_PRICE,
          this.programCompareAtPrice,
        )
      },
      [`@${UPDATE_ACTIVE_VARIANT}.window`]() {
        const activeVariant = this.$event.detail
        this.activeVariant = activeVariant

        setTimeout(() => {
          if (!this.isTracked) {
            chord?.trackProductViewed({
              cart: {},
              product: transformToChordItem(
                this.activeVariant,
                null,
                this.groupedAddonsWithQuantity,
                this.selectedOptionProperties.meals,
              ),
            })
            this.isTracked = true
          } else {
            if (!this.isFirstLoad && this.activeVariant.product) {
              chord?.trackProductClicked({
                cart: {},
                product: transformToChordItem(
                  this.activeVariant,
                  null,
                  this.groupedAddonsWithQuantity,
                  this.selectedOptionProperties.meals,
                ),
              })
            } else {
              this.isFirstLoad = false
            }
          }
        })

        this.$dispatch(UPDATE_PROGRAM_PRICE, this.programPrice)
        this.$dispatch(
          UPDATE_PROGRAM_COMPARE_AT_PRICE,
          this.programCompareAtPrice,
        )
      },
      [`@${UPDATE_ACTIVE_PROGRAM}.window`]() {
        const { label } = this.$event.detail
        this.selectedOptionProperties.programLength = label
      },
      [`@${UPDATE_ACTIVE_MEALS}.window`]() {
        const { meals } = this.$event.detail
        this.selectedOptionProperties.meals = meals
      },
      [`@${UPDATE_DELIVERY_WINDOW}.window`]() {
        const { properties, isZipCodeValid, selectedDate } = this.$event.detail
        if (!isZipCodeValid || !properties) {
          this.zipCode = null
          return
        }
        this.selectedDate = selectedDate
        this.deliveryProperties = properties
        this.zipCode = properties._zip_code
      },
      [`@${CART_INITIALISED}.window`]() {
        const cart = this.$event.detail
        this.cartZipCode = getCartZipCode(cart)
      },
      [`@${CART_UPDATED}.window`]() {
        const cart = this.$event.detail
        this.cartZipCode = getCartZipCode(cart)
        this.variantBeforeDowngrade = null
      },
      [`@${VARIANT_DOWNGRADED}.window`]() {
        const variant = this.$event.detail.variantBeforeDowngrade
        this.variantBeforeDowngrade = variant
      },
      [`@${INIT_BUYING_OPTION}.window`]() {
        this.activeBuyingOption = this.$event.detail
      },
    },

    get addonsPrice() {
      const groupedAddonsWithQuantity = this.groupedAddonsWithQuantity as {
        [groupdId: string]: {
          addons: {
            addon: CMSProgramAddonOption
            quantity: number
          }[]
          group: CMSAddonsOptionGroup
        }
      }
      const addonsWithQuantity = Object.values(
        groupedAddonsWithQuantity,
      ).flatMap(({ addons }) => addons)

      return addonsWithQuantity.reduce((acc, { addon, quantity }) => {
        return acc + addon.variant.price * quantity
      }, 0)
    },

    get variantToDisplayPrice() {
      return this.variantBeforeDowngrade || this.activeVariant
    },

    get programPrice() {
      return this.applyRedemption != false
        ? this.variantToDisplayPrice.price + this.addonsPrice - 75 // Hardcoded Price
        : this.variantToDisplayPrice.price + this.addonsPrice
    },

    get programCompareAtPrice() {
      if (!this.variantToDisplayPrice.compareAtPrice) return ''

      return this.variantToDisplayPrice.compareAtPrice + this.addonsPrice
    },

    get lineProperties() {
      const properties: string[] = []
      const { meals, programLength } = this.selectedOptionProperties
      if (meals) {
        const mealsArray = Array.isArray(meals) ? meals : [meals]
        const transformedMeals = mealsArray.map(
          (meal) => meal.charAt(0).toUpperCase() + meal.slice(1),
        )
        properties.push(transformedMeals.join(', '))
      }
      if (programLength) properties.push(programLength)
      if (
        this.selectedDate.isException &&
        this.activeBuyingOption === 'sub' &&
        this.variantBeforeDowngrade
      ) {
        const daysOfFood = getDaysOfFoodFromSku(this.variantBeforeDowngrade.sku)
        properties.push(`
          After the shortened holiday week, your program will be adjusted to ${daysOfFood} days a week, ${formatPrice(
            this.programPrice,
          )} weekly
        `)
      }
      return JSON.stringify(properties)
    },

    get programProperties(): null | Record<string, string | number> {
      if (!this.deliveryProperties) return null

      let properties = {
        ...this.deliveryProperties,
        _display_line_properties: this.lineProperties,
        _product_path: window.location.pathname,
        _default_sku: this.activeVariant.sku,
        _subscription_week: this.activeBuyingOption === 'sub' ? '1' : '0',
      }

      if (
        this.selectedDate.isException &&
        this.activeBuyingOption === 'sub' &&
        this.variantBeforeDowngrade
      ) {
        properties = {
          ...properties,
          _sku_week_2: this.variantBeforeDowngrade.sku,
        }
      }

      if (
        Object.values(this.groupedAddonsWithQuantity).flatMap(
          ({ addons }: any) => addons,
        ).length
      ) {
        properties = {
          ...properties,
          _addons: JSON.stringify(
            Object.values(this.groupedAddonsWithQuantity).flatMap(
              ({ addons }: any) =>
                addons.map(({ addon, quantity }: any) => ({
                  id: addon.variant.id,
                  quantity,
                })),
            ),
          ),
          _addon_groups: JSON.stringify(
            Object.values(this.groupedAddonsWithQuantity).map(
              ({ group }: any) => ({
                id: group._id,
                label: group.label,
                addonIds: group.options.map((option: any) => option.variant.id),
                validation: { max: group.optionGroupMaxCount },
              }),
            ),
          ),
        }
      }

      return properties
    },

    async addToCart() {
      this.addingToCart = true
      try {
        let redemptionProperties: { key: string; value: any }[] | [] = []

        if (this.applyRedemption) {
          try {
            const response = await fetch('/api/yotpo-redemption', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                redemption_option_id: 869132,
                customer_email: this.customer.email,
                delay_points_deduction: true,
                points_to_redeem: 1250,
              }),
            })

            const data = await response.json()

            if (data.token) {
              redemptionProperties = [
                { key: '_yotpo_coupon_code', value: data.reward_text },
                { key: '_swell_points_used', value: '1250' },
                { key: '_swell_redemption_id', value: data.id.toString() },
                { key: '_swell_redemption_token', value: data.token },
                { key: '_swell_discount_type', value: 'fixed_amount' },
                { key: '_swell_discount_amount', value: '7500' },
                { key: '_swell_partial_redemption', value: 'true' },
              ]
            }
          } catch (error) {
            captureException(error)
          }
        }

        if (!this.activeVariant) return console.error('No active variant')

        if (this.cartZipCode && this.cartZipCode !== this.zipCode) {
          this.$dispatch(SHOW_PROGRAM_INFO_MODAL, {
            content: DUAL_ZIP_CODE_MESSAGE,
          })
          return
        }

        if (
          this.selectedDate.isException &&
          !this.selectedOptionProperties?.programLength?.startsWith('2 Days') // TODO: Remove hardcoding
        ) {
          let programLength = 1

          if (
            (this.programType === 'signature' ||
              this.programType === 'campaign') &&
            this.activeBuyingOption === 'trial'
          ) {
            const daysOfFood = getDaysOfFoodFromSku(this.activeVariant.sku)
            programLength =
              Math.round(daysOfFood / this.programProperties._days_of_food) || 1
          }

          this.$dispatch(ALIGN_ADDED_SKU_WITH_HOLIDAY_SCHEDULE, {
            deliveries: this.selectedDate.exceptionDeliveries,
            programLength: programLength,
          })
        }

        const deliveryDates = await getCalculatedDeliveryDates(
          getWeekOfDate(this.programProperties._start_date),
          this.programProperties._start_date,
          this.programProperties._delivery_zone_id,
          this.programProperties._delivery_window_id,
          this.activeVariant.sku,
        )

        const programProperties = {
          ...this.programProperties,
          _program_delivery_dates: JSON.stringify(deliveryDates),
        }

        const itemsToAdd: {
          merchandiseId: string
          quantity: number
          sellingPlanId?: string
          attributes?: { key: string; value: string }[]
        }[] = [
          {
            merchandiseId: this.activeVariant.gid,
            quantity: 1,
            ...(variantIsSubscription(this.activeVariant.sku) && {
              sellingPlanId: `gid://shopify/SellingPlan/${MEAL_PROGRAM_SELLING_PLAN_ID}`,
            }),
            attributes:
              programProperties &&
              Object.keys(programProperties)
                .map((key: string) => {
                  return {
                    key,
                    value: programProperties[key],
                  }
                })
                .filter((attr) => attr.value)
                .concat(redemptionProperties),
          },
        ]

        if (
          Object.values(this.groupedAddonsWithQuantity).flatMap(
            ({ addons }: any) => addons,
          ).length
        ) {
          itemsToAdd.push(
            ...Object.values(this.groupedAddonsWithQuantity).flatMap(
              ({ addons }: any) =>
                addons.map(({ addon, quantity }: any) => ({
                  merchandiseId: addon.variant.gid,
                  quantity,
                  ...(variantIsSubscription(this.activeVariant.sku) && {
                    sellingPlanId: `gid://shopify/SellingPlan/${MEAL_PROGRAM_SELLING_PLAN_ID}`,
                  }),
                })),
            ),
          )
        }

        if (this.programConfig.infoModal?.isEnabled) {
          openDialog('program-info-modal')
        }

        const cart = await addItems([...itemsToAdd])

        try {
          //TODO: open dialog
          this.$dispatch(NOTICE_ADD_TO_CART, {
            cart,
            item: this.activeVariant,
            cartPopupItem: JSON.parse(JSON.stringify(this.activeVariant)),
            rawAddons: JSON.stringify(
              Object.values(this.groupedAddonsWithQuantity)
                .map((group: any) =>
                  group.addons.map(({ addon, quantity }: any) => ({
                    id: addon.variant.id,
                    quantity,
                  })),
                )
                .flat(),
            ),
            addonGroups: programProperties._addon_groups,
            programStartDateString: this.programProperties._start_date,
          })
          this.$dispatch(SHOW_CART_MODAL, {
            sectionId: this.programConfig.sectionId,
            sectionType: this.programConfig.sectionType,
          })
          chord?.trackProductAdded({
            cart: cart,
            product: transformToChordItem(
              this.activeVariant,
              null,
              this.groupedAddonsWithQuantity,
              this.selectedOptionProperties.meals,
            ),
          })
        } catch (error) {
          captureException(error)
        }

        if (this.selectedDate.isException && this.variantBeforeDowngrade) {
          this.activeVariant = this.variantBeforeDowngrade
          this.$dispatch(UPDATE_PROGRAM_PRICE, this.programPrice)
        }

        if (cart?.lines) {
          this.$dispatch(CART_UPDATED, cart)
        }
      } finally {
        this.addingToCart = false
      }
    },
  }))
})
